import { useState, useRef, useEffect } from 'react';
import { X, Calendar, User, Tag, Zap, Trash2, CreditCard as Edit3, Check, Search } from 'lucide-react';
import type { Task, TaskStatus, Priority, SubTask } from '../../types';
import { useApp } from '../../context/AppContext';
import { getStatusConfig, getPriorityConfig, formatDate } from '../../utils/helpers';
import Avatar from '../common/Avatar';
import Badge from '../common/Badge';
import { LabelBadge } from '../common/Badge';
import ConfirmDialog from '../common/ConfirmDialog';
import RichTextEditor from '../common/RichTextEditor';
import TaskComments from './TaskComments';
import TaskAttachments from './TaskAttachments';
import SubtaskList from './SubtaskList';

interface TaskModalProps {
  task: Task;
  onClose: () => void;
}

const statuses: TaskStatus[] = ['backlog', 'todo', 'in_progress', 'in_review', 'done'];
const priorities: Priority[] = ['low', 'medium', 'high', 'critical'];

export default function TaskModal({ task, onClose }: TaskModalProps) {
  const { dispatch, getUserById, getLabelById, state, apiUpdateTask, apiDeleteTask } = useApp();
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [editingDesc, setEditingDesc] = useState(false);
  const [desc, setDesc] = useState(task.description);
  const [activeTab, setActiveTab] = useState<'comments' | 'attachments' | 'subtasks'>('comments');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);

  const reporter = getUserById(task.reporterId);
  const assignees = task.assigneeIds.map((id) => getUserById(id)).filter(Boolean);
  const taskLabels = task.labelIds.map((id) => getLabelById(id)).filter(Boolean);
  const status = getStatusConfig(task.status);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(e.target as Node)
      ) {
        setShowAssigneeDropdown(false);
        setAssigneeSearch('');
      }
    }
    if (showAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssigneeDropdown]);

  const updateTask = (partial: Partial<Task>) => {
    apiUpdateTask({ id: task.id, ...partial });
  };

  const saveTitle = () => {
    if (title.trim()) updateTask({ title: title.trim() });
    setEditingTitle(false);
  };

  const saveDesc = () => {
    updateTask({ description: desc });
    setEditingDesc(false);
  };

  const filteredUsers = state.users.filter(
    (u) =>
      !task.assigneeIds.includes(u.id) &&
      u.name.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-2 md:px-4 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={task.title}
    >
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col animate-scale-in overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-xs font-mono text-slate-400 shrink-0">{task.id.toUpperCase()}</span>
            <Badge className={status.color}>{status.label}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              aria-label="Excluir tarefa"
            >
              <Trash2 size={18} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col lg:flex-row">
            <div className="flex-1 p-4 md:p-6 space-y-6 min-w-0">
              {editingTitle ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={saveTitle}
                    onKeyDown={(e) => e.key === 'Enter' && saveTitle()}
                    className="flex-1 text-xl font-semibold text-slate-800 px-2 py-1 border border-brand-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-300"
                    autoFocus
                  />
                  <button onClick={saveTitle} className="p-1.5 text-brand-500" aria-label="Salvar título">
                    <Check size={18} />
                  </button>
                </div>
              ) : (
                <div
                  className="group flex items-start gap-2 cursor-pointer"
                  onClick={() => setEditingTitle(true)}
                >
                  <h2 className="text-xl font-semibold text-slate-800 leading-tight">{task.title}</h2>
                  <Edit3
                    size={14}
                    className="mt-1.5 text-slate-300 transition-opacity shrink-0"
                  />
                </div>
              )}

              {editingDesc ? (
                <div>
                  <RichTextEditor value={desc} onChange={setDesc} />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={saveDesc}
                      className="px-3 py-1.5 rounded-lg bg-brand-500 text-white text-xs font-medium hover:bg-brand-600 transition-colors"
                    >
                      Salvar
                    </button>
                    <button
                      onClick={() => {
                        setDesc(task.description);
                        setEditingDesc(false);
                      }}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="group cursor-pointer"
                  onClick={() => setEditingDesc(true)}
                >
                  {task.description ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: task.description }}
                      className="prose prose-sm max-w-none text-slate-600"
                    />
                  ) : (
                    <p className="text-sm text-slate-400 italic">Clique para adicionar descrição...</p>
                  )}
                </div>
              )}

              <div className="border-t border-slate-100 pt-4">
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setActiveTab('comments')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'comments'
                      ? 'bg-brand-50 text-brand-600'
                      : 'text-slate-500 hover:bg-slate-50'
                      }`}
                  >
                    Comentários ({task.comments.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('attachments')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'attachments'
                      ? 'bg-brand-50 text-brand-600'
                      : 'text-slate-500 hover:bg-slate-50'
                      }`}
                  >
                    Anexos ({task.attachments.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('subtasks')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'subtasks'
                      ? 'bg-brand-50 text-brand-600'
                      : 'text-slate-500 hover:bg-slate-50'
                      }`}
                  >
                    Subtarefas ({task.subtasks.length})
                  </button>
                </div>

                {activeTab === 'comments' && (
                  <TaskComments taskId={task.id} comments={task.comments} />
                )}
                {activeTab === 'attachments' && (
                  <TaskAttachments taskId={task.id} attachments={task.attachments} />
                )}
                {activeTab === 'subtasks' && (
                  <SubtaskList
                    task={task}
                    onUpdate={(newSubtasks: SubTask[]) => updateTask({ subtasks: newSubtasks })}
                  />
                )}
              </div>
            </div>

            <div className="lg:w-72 border-t lg:border-t-0 lg:border-l border-slate-100 p-4 md:p-6 space-y-5 bg-slate-50/50 shrink-0">
              <SidebarField icon={Zap} label="Status">
                <select
                  value={task.status}
                  onChange={(e) => updateTask({ status: e.target.value as TaskStatus })}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                  aria-label="Status da tarefa"
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {getStatusConfig(s).label}
                    </option>
                  ))}
                </select>
              </SidebarField>

              <SidebarField icon={Zap} label="Prioridade">
                <select
                  value={task.priority}
                  onChange={(e) => updateTask({ priority: e.target.value as Priority })}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                  aria-label="Prioridade da tarefa"
                >
                  {priorities.map((p) => (
                    <option key={p} value={p}>
                      {getPriorityConfig(p).label}
                    </option>
                  ))}
                </select>
              </SidebarField>

              <SidebarField icon={User} label="Responsáveis">
                <div className="space-y-2">
                  {assignees.map((user) => (
                    <div key={user!.id} className="flex items-center gap-2">
                      <Avatar name={user!.name} color={user!.avatarColor} size="sm" />
                      <span className="text-sm text-slate-600 flex-1">{user!.name}</span>
                      <button
                        onClick={() => updateTask({ assigneeIds: task.assigneeIds.filter(id => id !== user!.id) })}
                        className="p-0.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        aria-label={`Remover ${user!.name}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <div ref={assigneeDropdownRef} className="relative">
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-brand-300 focus-within:border-brand-400 transition-all">
                      <Search size={14} className="text-slate-400 shrink-0" />
                      <input
                        type="text"
                        value={assigneeSearch}
                        onChange={(e) => {
                          setAssigneeSearch(e.target.value);
                          setShowAssigneeDropdown(true);
                        }}
                        onFocus={() => setShowAssigneeDropdown(true)}
                        placeholder="Buscar responsável..."
                        className="w-full bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400"
                        aria-label="Buscar responsável"
                      />
                    </div>
                    {showAssigneeDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                        <div className="max-h-48 overflow-y-auto py-1">
                          {filteredUsers.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                updateTask({ assigneeIds: [...task.assigneeIds, user.id] });
                                setAssigneeSearch('');
                                setShowAssigneeDropdown(false);
                              }}
                              className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-slate-50 transition-colors text-slate-700"
                            >
                              <Avatar name={user.name} color={user.avatarColor} size="sm" />
                              <span className="truncate">{user.name}</span>
                            </button>
                          ))}
                          {filteredUsers.length === 0 && (
                            <div className="px-3 py-3 text-xs text-slate-400 text-center">
                              Nenhum usuário encontrado
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </SidebarField>

              <SidebarField icon={User} label="Relator">
                {reporter && (
                  <div className="flex items-center gap-2">
                    <Avatar name={reporter.name} color={reporter.avatarColor} size="sm" />
                    <span className="text-sm text-slate-600">{reporter.name}</span>
                  </div>
                )}
              </SidebarField>

              <SidebarField icon={Tag} label="Etiquetas">
                <div className="flex flex-wrap gap-1">
                  {taskLabels.map((label) => (
                    <span key={label!.id} className="inline-flex items-center gap-1">
                      <LabelBadge name={label!.name} color={label!.color} />
                      <button
                        onClick={() => updateTask({ labelIds: task.labelIds.filter(id => id !== label!.id) })}
                        className="p-0.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        aria-label={`Remover ${label!.name}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  <select
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && !task.labelIds.includes(val)) {
                        updateTask({ labelIds: [...task.labelIds, val] });
                      }
                      e.target.value = '';
                    }}
                    className="w-full mt-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                    defaultValue=""
                    aria-label="Adicionar etiqueta"
                  >
                    <option value="" disabled>
                      Adicionar etiqueta...
                    </option>
                    {state.labels
                      .filter((l) => !task.labelIds.includes(l.id))
                      .map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                  </select>
                </div>
              </SidebarField>

              <SidebarField icon={Zap} label="Story Points">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={task.storyPoints}
                  onChange={(e) => updateTask({ storyPoints: parseInt(e.target.value) || 0 })}
                  className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                  aria-label="Story points"
                />
              </SidebarField>

              <SidebarField icon={Calendar} label="Prazo">
                <input
                  type="date"
                  value={task.dueDate ? task.dueDate.split('T')[0] : ''}
                  onChange={(e) =>
                    updateTask({
                      dueDate: e.target.value ? `${e.target.value}T23:59:59Z` : null,
                    })
                  }
                  className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                  aria-label="Data de prazo"
                />
              </SidebarField>

              <div className="pt-3 border-t border-slate-200">
                <p className="text-xs text-slate-400">
                  Criado em {formatDate(task.createdAt)}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Atualizado em {formatDate(task.updatedAt)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Excluir tarefa"
        message="Tem certeza que deseja excluir esta tarefa? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        onConfirm={() => {
          apiDeleteTask(task.id);
          onClose();
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}

function SidebarField({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={14} className="text-slate-400" />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}
